import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/dialog";
import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import type { IQuestion } from "@/apis/exam_papers/types";

// Extended question type with module information
interface IQuestionWithModule extends IQuestion {
  moduleIndex?: number;
  moduleQuestionIndex?: number;
}

interface FilteringQuestionsModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedIds: string[]) => void;
  questions: IQuestionWithModule[];
  selectedQuestionIds: string[];
}

const FilteringQuestionsModal: React.FC<FilteringQuestionsModalProps> = ({
  open,
  onClose,
  onApply,
  questions,
  selectedQuestionIds,
}) => {
  // Temporary selection state for modal
  const [tempSelectedIds, setTempSelectedIds] =
    useState<string[]>(selectedQuestionIds);

  // Get question number display
  const getQuestionNumber = (question: IQuestionWithModule, index: number) => {
    if (question.moduleIndex && question.moduleQuestionIndex) {
      return `Module ${question.moduleIndex} - Q${question.moduleQuestionIndex}`;
    }
    return `Question ${index + 1}`;
  };

  // Update tempSelectedIds when modal opens with current selection
  useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedQuestionIds);
    }
  }, [open, selectedQuestionIds]);

  const handleToggleQuestion = (questionId: string) => {
    setTempSelectedIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSelectAll = () => {
    setTempSelectedIds(questions.map((q) => q.id));
  };

  const handleDeselectAll = () => {
    setTempSelectedIds([]);
  };

  const handleApply = () => {
    onApply(tempSelectedIds);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedIds(selectedQuestionIds);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} size="lg">
      <DialogTitle>Select Questions to Print</DialogTitle>
      <DialogBody className="space-y-4">
        {/* Selection Controls */}
        <div className="flex gap-2 mb-4">
          <Button onClick={handleSelectAll} plain>
            Select All
          </Button>
          <Button onClick={handleDeselectAll} plain>
            Deselect All
          </Button>
          <div className="text-sm text-gray-600 flex items-center ml-auto">
            {tempSelectedIds.length} of {questions.length} questions selected
          </div>
        </div>

        {/* Questions List */}
        <div className="max-h-96 overflow-y-auto space-y-3">
          {questions.map((question, index) => (
            <div
              key={question.id}
              className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => handleToggleQuestion(question.id)}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={tempSelectedIds.includes(question.id)}
                  onChange={() => handleToggleQuestion(question.id)}
                  className="mt-1"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 mb-1">
                  {getQuestionNumber(question, index)}
                </div>
                <div
                  className="quill-content text-sm text-gray-600 line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html:
                      question.question && question.question.length > 100
                        ? question.question.substring(0, 100) + "..."
                        : question.question || "No question text",
                  }}
                />
                {question.passage && (
                  <div className="text-xs text-blue-600 mt-1">Has passage</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleApply}>Apply Selection</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilteringQuestionsModal;
